import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Row, Col, Button, Form, Input, notification, Card, Space, Typography, Alert,
} from 'antd';
import {
  MailOutlined,
  MessageOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import './ForgotPassword.scss';
import Background from '../sharedComponents/Background/Background';
import logo from '../../assets/images/CooperLogo.png';
import {
  get, post, ResponseWrapper, RequestWrapper, isSuperAgentError,
} from '../../dataSource/requests';

type RecoveryMethod = 'sms' | 'email';

interface RequestTokenForm {
  federalRegistration: string;
}

interface ResetPasswordForm {
  token: string;
  password: string;
  confirmPassword: string;
}

const normalizeFederalRegistration = (value: string): string => {
  return value.replace(/\D/g, '');
};

const getRequestErrorMessage = (code?: number): string => {
  switch (code) {
    case -1042:
      return 'O CPF ou CNPJ deve ser informado.';
    case -1021:
      return 'CPF/CNPJ não encontrado.';
    case -1017:
      return 'Nenhum email cadastrado para esse documento.';
    case -1018:
      return 'Nenhum telefone cadastrado para esse documento.';
    case -5021:
      return 'Telefone definido no cadastro tem mais dígitos que o padrão.';
    case -5022:
      return 'Telefone definido no cadastro tem menos dígitos que o padrão.';
    default:
      return 'Aconteceu algum problema durante a requisição.';
  }
};

const getChangePasswordErrorMessage = (code?: number): string => {
  switch (code) {
    case -1021:
      return 'CPF/CNPJ não encontrado.';
    case -1026:
      return 'Token não pode ser vazio.';
    case -1049:
      return 'Senha com menos de 6 dígitos.';
    case -1023:
      return 'Token inválido! O token enviado não está correto. Verifique e tente novamente.';
    case -1024:
      return 'Token expirado.';
    case -1048:
      return 'Token já validado.';
    default:
      return 'Aconteceu algum problema durante a requisição.';
  }
};

const ForgotPassword = (): JSX.Element => {
  const navigate = useNavigate();
  const [requestForm] = Form.useForm<RequestTokenForm>();
  const [resetForm] = Form.useForm<ResetPasswordForm>();

  const [loadingRequest, setLoadingRequest] = useState<boolean>(false);
  const [loadingReset, setLoadingReset] = useState<boolean>(false);

  const [method, setMethod] = useState<RecoveryMethod | null>(null);
  const [requestCompleted, setRequestCompleted] = useState<boolean>(false);
  const [passwordChanged, setPasswordChanged] = useState<boolean>(false);

  const [recipient, setRecipient] = useState<string>('');
  const [requestError, setRequestError] = useState<string>('');
  const [changeError, setChangeError] = useState<string>('');

  const [federalRegistrationValue, setFederalRegistrationValue] = useState<string>('');

  const handleMethodSelection = (selectedMethod: RecoveryMethod): void => {
    setMethod(selectedMethod);
    setRequestCompleted(false);
    setPasswordChanged(false);
    setRequestError('');
    setChangeError('');
    setRecipient('');
    requestForm.resetFields();
    resetForm.resetFields();
  };

  const handleRequestToken = async (values: RequestTokenForm): Promise<void> => {
    setLoadingRequest(true);
    setRequestError('');

    const normalizedFederalRegistration = normalizeFederalRegistration(values.federalRegistration);

    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    const requestReference: RequestWrapper = get({
      url: `/auth/forgotPasswordByFederalRegistration/${normalizedFederalRegistration}/${method === 'sms'}`,
      noAuth: true,
      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    try {
      response = await requestReference;
    } catch (error: unknown) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    setLoadingRequest(false);

    switch (true) {
      case (response?.body?.code === 1020):
      case (response?.body?.code === 1021): {
        const targetRecipient = response?.body?.response
          || response?.body?.jsonResponse?.response
          || '';

        setFederalRegistrationValue(normalizedFederalRegistration);
        setRecipient(targetRecipient);
        setRequestCompleted(true);

        notification.success({
          message: 'Código enviado!',
          description: 'Um código de 6 dígitos foi enviado para recuperação de senha.',
        });
        break;
      }

      case (genericError?.message?.includes('has been aborted')):
        break;

      default: {
        const errorMessage = getRequestErrorMessage(responseError?.body?.code);
        setRequestError(errorMessage);
        break;
      }
    }
  };

  const handlePasswordReset = async (values: ResetPasswordForm): Promise<void> => {
    setLoadingReset(true);
    setChangeError('');

    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    const requestReference: RequestWrapper = post({
      url: '/auth/validateTokenAndSetPassword',
      noAuth: true,
      params: {
        federalRegistration: federalRegistrationValue,
        token: values.token,
        password: values.password,
      },
      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    try {
      response = await requestReference;
    } catch (error: unknown) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    setLoadingReset(false);

    switch (true) {
      case (response?.body?.code === 1008):
      case (response?.status === 200):
        setPasswordChanged(true);
        notification.success({
          message: 'Senha alterada com sucesso',
          description: 'Agora você pode fazer login com a sua nova senha.',
        });
        break;

      case (genericError?.message?.includes('has been aborted')):
        break;

      default: {
        const errorMessage = getChangePasswordErrorMessage(responseError?.body?.code);
        setChangeError(errorMessage);
        break;
      }
    }
  };

  const renderMethodChooser = (): JSX.Element => {
    return (
      <div className="ForgotPassword__methodChooser">
        <h3 className="ForgotPassword__title">Esqueceu a senha?</h3>
        <p className="ForgotPassword__texto">Escolha um método de recuperação da senha:</p>

        <Space direction="vertical" size={16} className="ForgotPassword__methodList">
          <Card
            hoverable
            className="ForgotPassword__methodCard"
            onClick={(): void => handleMethodSelection('sms')}
          >
            <Space>
              <MessageOutlined />
              <Typography.Text strong>SMS</Typography.Text>
            </Space>
          </Card>

          <Card
            hoverable
            className="ForgotPassword__methodCard"
            onClick={(): void => handleMethodSelection('email')}
          >
            <Space>
              <MailOutlined />
              <Typography.Text strong>Email</Typography.Text>
            </Space>
          </Card>
        </Space>
      </div>
    );
  };

  const renderRequestForm = (): JSX.Element => {
    const title = method === 'sms' ? 'Recuperação de senha por SMS' : 'Recuperação de senha por Email';
    const description = method === 'sms'
      ? 'Insira o seu CPF/CNPJ e prossiga para receber um código por SMS no seu telefone cadastrado na Coopercitrus.'
      : 'Insira o seu CPF/CNPJ e prossiga para receber um código de recuperação no seu email cadastrado na Coopercitrus.';

    return (
      <>
        <h3 className="ForgotPassword__title">{title}</h3>
        <p className="ForgotPassword__texto">{description}</p>

        <Form
          scrollToFirstError
          className="ForgotPassword__form"
          form={requestForm}
          onFinish={handleRequestToken}
          noValidate
          autoComplete="off"
        >
          <Form.Item
            className="ForgotPassword__FormItemEmail"
            name="federalRegistration"
            rules={[
              { required: true, message: 'CPF/CNPJ é obrigatório.' },
              {
                validator: (_, value: string): Promise<void> => {
                  const digits = normalizeFederalRegistration(value || '');

                  if (!digits) {
                    return Promise.resolve();
                  }

                  if (digits.length !== 11 && digits.length !== 14) {
                    return Promise.reject(new Error('CPF/CNPJ deve ter exatamente 11 ou 14 dígitos.'));
                  }

                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input
              className="ForgotPassword__EmailInput"
              placeholder="CPF/CNPJ"
              maxLength={18}
            />
          </Form.Item>

          {requestError && (
            <Alert
              className="ForgotPassword__inlineAlert"
              type="error"
              message={requestError}
              showIcon
            />
          )}

          <Form.Item className="ForgotPassword__FormItemButton">
            <Button
              type="primary"
              htmlType="submit"
              className="ForgotPassword__FormButton"
              loading={loadingRequest}
            >
              ENVIAR CÓDIGO
            </Button>
          </Form.Item>
        </Form>
      </>
    );
  };

  const renderTokenValidationForm = (): JSX.Element => {
    const recipientText = recipient ? recipient.toLowerCase() : '';

    return (
      <>
        <h3 className="ForgotPassword__title">Validação de token</h3>

        <p className="ForgotPassword__texto">
          {recipientText
            ? `Código enviado para ${recipientText}. Em instantes você receberá um código de 6 dígitos.`
            : 'Em instantes você receberá um código de 6 dígitos no meio informado.'}
        </p>

        <p className="ForgotPassword__texto">
          Digite o código recebido e a nova senha para concluir a alteração.
        </p>

        <Form
          scrollToFirstError
          className="ForgotPassword__form"
          form={resetForm}
          onFinish={handlePasswordReset}
          autoComplete="off"
        >
          <Form.Item
            name="token"
            rules={[
              { required: true, message: 'Por favor, insira o código de validação.' },
              {
                pattern: /^\d{6}$/,
                message: 'O código deve conter 6 dígitos numéricos.',
              },
            ]}
          >
            <Input
              maxLength={6}
              placeholder="Código"
              inputMode="numeric"
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[
              { required: true, message: 'Por favor, insira a nova senha.' },
              { min: 6, message: 'A senha deve ter no mínimo 6 caracteres.' },
              { max: 150, message: 'Máximo de 150 caracteres.' },
            ]}
          >
            <Input.Password
              maxLength={150}
              placeholder="Nova senha"
            />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            dependencies={['password']}
            rules={[
              { required: true, message: 'Por favor, confirme a nova senha.' },
              ({ getFieldValue }): { validator: (_: unknown, value: string) => Promise<void> } => ({
                validator(_, value): Promise<void> {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }

                  return Promise.reject(new Error('Nova senha e confirmar nova senha devem ser iguais.'));
                },
              }),
            ]}
          >
            <Input.Password
              maxLength={150}
              placeholder="Confirmar nova senha"
            />
          </Form.Item>

          {changeError && (
            <Alert
              className="ForgotPassword__inlineAlert"
              type="error"
              message={changeError}
              showIcon
            />
          )}

          <Form.Item className="ForgotPassword__FormItemButton">
            <Button
              type="primary"
              htmlType="submit"
              className="ForgotPassword__FormButton"
              loading={loadingReset}
            >
              ALTERAR SENHA
            </Button>
          </Form.Item>
        </Form>
      </>
    );
  };

  const renderSuccess = (): JSX.Element => {
    return (
      <div className="ForgotPassword__successContainer">
        <CheckCircleOutlined className="ForgotPassword__successIcon" />
        <h3 className="ForgotPassword__title">Senha alterada com sucesso</h3>
        <p className="ForgotPassword__texto">Agora você pode voltar para a tela de login e acessar com sua nova senha.</p>

        <Button
          type="primary"
          className="ForgotPassword__FormButton"
          onClick={(): void => {
            navigate('/');
          }}
        >
          VOLTAR PARA O LOGIN
        </Button>
      </div>
    );
  };

  return (
    <div className="ForgotPassword">
      <Row>
        <Col xs={{ span: 0 }} md={{ span: 14 }} className="ForgotPassword__background">
          <Background />
        </Col>
        <Col xs={{ span: 24 }} md={{ span: 10 }} className="ForgotPassword__content">
          <div className="ForgotPassword__container">
            <img className="ForgotPassword__logo-image" src={logo} alt="" />

            {!method && !requestCompleted && !passwordChanged && renderMethodChooser()}
            {method && !requestCompleted && !passwordChanged && renderRequestForm()}
            {method && requestCompleted && !passwordChanged && renderTokenValidationForm()}
            {passwordChanged && renderSuccess()}

            {!passwordChanged && (
              <div className="ForgotPassword__linksContainer">
                {method && (
                  <Button
                    type="link"
                    className="ForgotPassword__voltar"
                    onClick={(): void => {
                      handleMethodSelection(method === 'sms' ? 'email' : 'sms');
                    }}
                  >
                    {method === 'sms' ? 'tentar usando Email' : 'tentar usando SMS'}
                  </Button>
                )}

                <Button
                  className="ForgotPassword__voltar"
                  type="link"
                  onClick={(): void => {
                    navigate('/');
                  }}
                >
                  voltar para o login
                </Button>
              </div>
            )}
          </div>
        </Col>
      </Row>
    </div>
  );
};

ForgotPassword.propTypes = {
};

ForgotPassword.defaultProps = {
};

export default ForgotPassword;
